import type {IContentItem, Elements} from '@kontent-ai/delivery-sdk'
import {useState, useEffect} from 'react'
import {useRouter} from 'next/router'
import {clsx} from 'clsx'
import type {Block} from '@/_new-code/services/kontent-ai/types'
import {getBrandColor} from '@/_new-code/products/flexible-web-toolkit/blocks/helpers'

type QuantitySelectorContentItem = IContentItem<{
	quantity: Elements.TextElement
	elancoProductId: Elements.TextElement
}>

type SizeSelectorContentItem = IContentItem<{
	size: Elements.TextElement
	quantitySelector: Elements.LinkedItemsElement<QuantitySelectorContentItem>
}>

export type SkuProviderContentItem = IContentItem<{
	sizeSelector: Elements.LinkedItemsElement<SizeSelectorContentItem>
}>

export const SkuProviderBlock: Block<SkuProviderContentItem> = ({
	block,
	page,
}) => {
	const pageConfig = page.elements.config[0]
	const brandColor = pageConfig ? getBrandColor(pageConfig) : null
	const borderColor = brandColor ? `border-${brandColor}` : 'border-primary'
	const selectborderColor = brandColor
		? `border-${brandColor} border-3`
		: 'border-primary border-3'
	const router = useRouter()

	// Prepare sizes and quantities
	const sizes = (
		Array.isArray(block.elements.sizeSelector)
			? block.elements.sizeSelector
			: []
	).map((sizeItem) => ({
		size: sizeItem.elements.size,
		quantities: (Array.isArray(sizeItem.elements.quantitySelector)
			? sizeItem.elements.quantitySelector
			: []
		).map((qtyItem) => ({
			quantity: qtyItem.elements.quantity,
			productId: qtyItem.elements.elancoProductId,
			system: qtyItem.system,
		})),
		system: sizeItem.system,
	}))

	const [selectedSizeIdx, setSelectedSizeIdx] = useState(0)
	const [selectedQuantityIdx, setSelectedQuantityIdx] = useState(0)

	const selectedSize = sizes[selectedSizeIdx]
	const quantities = selectedSize?.quantities ?? []
	const selectedQuantity = quantities[selectedQuantityIdx]
	const elancoProductId = selectedQuantity?.productId

	const showSizeSection = sizes.length > 1

	// Reset quantity when size changes
	useEffect(() => {
		setSelectedQuantityIdx(0)
	}, [selectedSizeIdx])

	// Update router SKU when quantity changes
	useEffect(() => {
		const currentSku = router.query.sku
		if (elancoProductId && currentSku !== elancoProductId) {
			void router.replace(
				{
					pathname: router.asPath.split('?')[0],
					query: {sku: elancoProductId},
				},
				undefined,
				{shallow: true}
			)
		}
	}, [elancoProductId, router])

	return (
		<div className="flex flex-col items-start gap-6 pl-8">
			{/* Size Selector */}
			{showSizeSection ? (
				<div>
					<div className="mb-2 text-sm font-semibold">Pet weight</div>
					<div className="flex flex-wrap gap-4">
						{sizes.map((s, idx) => (
							<button
								className={clsx(
									'flex min-w-[64px] items-center justify-center rounded-lg border px-3 py-2 text-lg font-medium transition-all duration-150',
									borderColor,
									selectedSizeIdx === idx
										? [
												selectborderColor,
												'font-bold shadow',
											]
										: []
								)}
								data-kontent-element-codename="size"
								data-kontent-item-id={s.system.id}
								key={s.size}
								onClick={() => {
									setSelectedSizeIdx(idx)
									setSelectedQuantityIdx(0)
								}}
								type="button"
							>
								{s.size}
							</button>
						))}
					</div>
				</div>
			) : null}

			{/* Quantity Selector */}
			{quantities.length > 0 && (
				<div>
					<div className="mb-2 text-sm font-semibold">Quantity</div>
					<div className="flex flex-wrap gap-4">
						{quantities.map((q, idx) => (
							<button
								className={clsx(
									'flex min-w-[64px] items-center justify-center rounded-lg border px-3 py-2 text-lg font-medium transition-all duration-150',
									borderColor,
									selectedQuantityIdx === idx
										? [
												selectborderColor,
												'font-bold shadow',
											]
										: []
								)}
								data-kontent-element-codename="quantity"
								data-kontent-item-id={q.system.id}
								key={q.quantity}
								onClick={() => {
									setSelectedQuantityIdx(idx)
								}}
								type="button"
							>
								{q.quantity}
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	)
}
